import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { PrimaryButton, SecondaryButton } from "../../components";
import { useArticles } from "../../store/useArticles";
import { useDropLocations } from "../../store/useDropLocations";
import { useAlert } from "../../store/useAlert";

interface PinnableArticle {
  _id?: string;
  title?: string;
  cities?: string[];
  locationIds?: string[];
  pinnedCities?: string[];
  pinnedLocationIds?: string[];
}

interface Props {
  isOpen: boolean;
  article: PinnableArticle | null;
  onClose: () => void;
}

/**
 * Picks where an article takes the app's TRENDING slot.
 *
 * The choices are limited to places the article is actually readable, because
 * the app resolves the pin and the article list independently: pinning to a city
 * the article isn't published in would show it as Trending to people who can't
 * find it anywhere else. Concretely, an article is readable to a user when its
 * `cities` covers their city or its `locationIds` covers their exact store, so:
 *   - pinnable cities  = the article's own cities
 *   - pinnable stores  = its own stores, plus every store inside its cities
 */
export const PinArticleModal = ({ isOpen, article, onClose }: Props) => {
  const { pinArticle } = useArticles();
  const { dropLocations, getDropLocations } = useDropLocations();
  const { setAlert } = useAlert();

  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [isPinning, setIsPinning] = useState(false);

  useEffect(() => {
    if (isOpen && !dropLocations?.length) getDropLocations();
  }, [isOpen, dropLocations?.length, getDropLocations]);

  // Re-seed whenever a different article opens the modal, so editing an existing
  // pin starts from its current targets instead of a blank slate.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedCities(article?.pinnedCities ?? []);
    setSelectedLocationIds(article?.pinnedLocationIds ?? []);
  }, [isOpen, article?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const articleCities = useMemo(() => article?.cities ?? [], [article?.cities]);
  const articleLocationIds = useMemo(
    () => (article?.locationIds ?? []).map(String),
    [article?.locationIds]
  );

  const pinnableCities = useMemo(
    () => [...articleCities].sort((a, b) => a.localeCompare(b)),
    [articleCities]
  );

  const pinnableLocations = useMemo(
    () =>
      (dropLocations ?? [])
        .filter((location) => {
          if (!location?._id) return false;
          // Its own stores, plus any store the article already reaches via a city.
          if (articleLocationIds.includes(String(location._id))) return true;
          return Boolean(location.city && articleCities.includes(location.city));
        })
        .sort((a, b) =>
          (a.cost_place ?? a.city ?? "").localeCompare(
            b.cost_place ?? b.city ?? ""
          )
        ),
    [dropLocations, articleCities, articleLocationIds]
  );

  if (!isOpen || !article?._id) return null;

  const toggle = (list: string[], value: string) =>
    list.includes(value)
      ? list.filter((item) => item !== value)
      : [...list, value];

  const selectedCount = selectedCities.length + selectedLocationIds.length;
  const hasNowhereToPin = !pinnableCities.length && !pinnableLocations.length;

  const handlePin = async () => {
    if (!selectedCount) {
      setAlert({
        isOpen: true,
        message: "Pick at least one city or store to pin this article to.",
        severity: "error",
      });
      return;
    }
    setIsPinning(true);
    const ok = await pinArticle(
      article._id as string,
      selectedCities,
      selectedLocationIds
    );
    setIsPinning(false);
    if (ok) onClose();
  };

  return createPortal(
    <>
      <div className="opacity-25 fixed inset-0 z-[99998] bg-black" />
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-[99999] outline-none focus:outline-none">
        <div className="relative w-11/12 sm:w-2/3 lg:w-1/2 max-w-xl my-6 mx-auto">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            <div className="px-6 pt-6 pb-2">
              <p className="font-satoshi text-lg font-bold text-dark">
                Pin as Trending
              </p>
              <p className="font-satoshi text-sm text-gray-600 mt-1 line-clamp-2">
                {article.title}
              </p>
              <p className="font-satoshi text-xs text-gray-500 mt-2">
                Readers see this as the Trending card when it matches their
                default location. A store beats a city, and each place holds only
                one pinned article. You can pick cities and stores together.
              </p>
            </div>

            {hasNowhereToPin ? (
              <div className="px-6 py-6">
                <p className="font-satoshi text-sm text-gray-600">
                  This article isn't published to any city or store yet, so there
                  is nowhere to pin it. Add a city or a store to the article
                  first.
                </p>
              </div>
            ) : (
              <div className="px-6 py-4 max-h-72 overflow-y-auto">
                {pinnableCities.length ? (
                  <div className="mb-4">
                    <p className="font-satoshi text-sm font-medium text-dark mb-2">
                      Cities
                    </p>
                    <div className="flex flex-col gap-1">
                      {pinnableCities.map((city) => (
                        <label
                          key={city}
                          className="flex items-center gap-2 font-satoshi text-sm text-dark py-1"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCities.includes(city)}
                            onChange={() =>
                              setSelectedCities((prev) => toggle(prev, city))
                            }
                          />
                          {city}
                        </label>
                      ))}
                    </div>
                  </div>
                ) : null}

                {pinnableLocations.length ? (
                  <div>
                    <p className="font-satoshi text-sm font-medium text-dark mb-2">
                      Stores
                    </p>
                    <div className="flex flex-col gap-1">
                      {pinnableLocations.map((location) => (
                        <label
                          key={location._id}
                          className="flex items-center gap-2 font-satoshi text-sm text-dark py-1"
                        >
                          <input
                            type="checkbox"
                            checked={selectedLocationIds.includes(
                              String(location._id)
                            )}
                            onChange={() =>
                              setSelectedLocationIds((prev) =>
                                toggle(prev, String(location._id))
                              )
                            }
                          />
                          <span>
                            {location.cost_place ?? location.address}
                            <span className="text-gray-500">
                              {" "}
                              · {location.city}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <SecondaryButton label="Cancel" onClick={onClose} />
              <PrimaryButton
                label={isPinning ? "Pinning..." : `Pin (${selectedCount})`}
                disabled={isPinning || !selectedCount || hasNowhereToPin}
                onClick={handlePin}
              />
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};
